import {useEffect, useState} from 'react'
import {getCountries} from '../../services/componentsService'

// Single, API-backed country source for the whole app (B1.5). Replaces the divergent hardcoded
// sources (memberships/data/countries.json, the orphaned auth json, and the inline <select> in
// ProfileDetails). Consumers disagree on what they persist — memberships stores the ISO `code`,
// experience-booking stores the `name` (fix 9aaef79) — so this exposes BOTH and lets each consumer
// keep its own stored representation.
export interface Country {
  code: string
  name: string
}

// Tolerate any /countries/ payload shape: a bare array (current backend, pagination_class=None), a
// paginated {results:[...]}, and either key order ({name,code} / {id,name,code}).
export const normalizeCountries = (data: any): Country[] => {
  const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []
  return arr
    .map((c: any) => ({
      code: String(c?.code ?? '').toUpperCase(),
      name: String(c?.name ?? ''),
    }))
    .filter((c: Country) => c.code !== '' && c.name !== '')
}

// Find a country by the value a consumer previously stored (name OR legacy code) — preserves the
// legacy-code fallback added in fix 9aaef79 so old records still resolve after consolidation.
export const findCountry = (countries: Country[], value?: string | null): Country | undefined => {
  if (!value) return undefined
  const v = String(value).trim().toLowerCase()
  return (
    countries.find((c) => c.name.toLowerCase() === v) ||
    countries.find((c) => c.code.toLowerCase() === v)
  )
}

// Fetches the full country catalog (~250 rows) once on mount and returns it normalized. The list is
// small, so consumers filter client-side for autocomplete instead of hitting the API per keystroke.
export const useCountries = () => {
  const [countries, setCountries] = useState<Country[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getCountries({search: '', limit: 1000, offset: 0})
        if (mounted) setCountries(normalizeCountries(data))
      } catch (err) {
        if (mounted) setError(err as Error)
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  return {countries, loading, error}
}
